<template>
    <div class="veckooversikt-list">
        <template v-for="(anmalan, index) in group.anmalningar">
            <div v-if="isVisible('dag')" :key="`dag-${index}`" class="vg-cell">
                {{ getDay(anmalan.bokad_operation_datum) }}<br />
                {{ anmalan.bokad_operation_datum }}
            </div>
            <div v-if="isVisible('optid')" :key="`optid-${index}`" class="vg-cell">
                {{ anmalan.current_operation_tid?.substring(0, 5) }}
            </div>
            <div v-if="isVisible('sal')" :key="`sal-${index}`" class="vg-cell">
                {{ anmalan.bokad_sal_display?.namn }}
            </div>
            <div v-if="isVisible('operator')" :key="`operator-${index}`" class="vg-cell">
                {{ anmalan.bokad_huvudoperator_display?.user_display?.name }}
            </div>
            <div v-if="isVisible('typoperation')" :key="`typoperation-${index}`" class="vg-cell">
                <span v-if="anmalan.typoperation_display">
                    {{ anmalan.typoperation_display.namn }}
                </span>
                <span
                    v-else
                    v-tooltip="{
                        content:
                            anmalan?.huvudoperationskod_display?.code +
                                ' ' +
                                anmalan?.huvudoperationskod_display?.displayName ?? '',
                        theme: 'kliniken-tooltip',
                    }"
                >
                    {{ anmalan.huvudoperationskod_display.code }}
                    {{
                        (anmalan.huvudoperationskod_display.displayName || "").length > 35
                            ? (anmalan.huvudoperationskod_display.displayName || "").slice(0, 35) + "..."
                            : anmalan.huvudoperationskod_display.displayName || ""
                    }}
                </span>
            </div>
            <div v-if="isVisible('huvudopkod')" :key="`huvudopkod-${index}`" class="vg-cell">
                <span
                    v-tooltip="{
                        content:
                            anmalan?.huvudoperationskod_display?.code +
                                ' ' +
                                anmalan?.huvudoperationskod_display?.displayName ?? '',
                        theme: 'kliniken-tooltip',
                    }"
                >
                    {{ anmalan.huvudoperationskod_display.code }}
                </span>
            </div>
            <div v-if="isVisible('nivaer')" :key="`nivaer-${index}`" class="vg-cell">
                <ul>
                    <li v-for="(niva_fran_till, idx) in anmalan.niva_fran_till_display" :key="idx">
                        {{ niva_fran_till.fran }} - {{ niva_fran_till.till }}
                    </li>
                </ul>
            </div>
            <div v-if="isVisible('sida')" :key="`sida-${index}`" class="vg-cell">
                {{ anmalan.lateralitet_display }}
            </div>
            <div v-if="isVisible('implantat')" :key="`implantat-${index}`" class="vg-cell">
                <ul>
                    <li v-for="(implantat, idx) in anmalan.implantat_display" :key="idx">
                        {{ implantat.namn }}
                    </li>
                </ul>
            </div>
            <div v-if="isVisible('resurser')" :key="`resurser-${index}`" class="vg-cell">
                <ul>
                    <li v-for="(resurs, idx) in anmalan.resurser_display" :key="idx">
                        {{ getResursWithStatus(resurs) }}
                    </li>
                </ul>
            </div>
            <div v-if="isVisible('reop')" :key="`reop-${index}`" class="vg-cell">
                {{ anmalan.reop ? "Ja" : "Nej" }}
            </div>
            <div v-if="isVisible('operationsinfo')" :key="`operationsinfo-${index}`" class="vg-cell">
                <ul>
                    <li v-if="anmalan.operationsinformation">{{ anmalan.operationsinformation }}</li>
                    <li v-if="anmalan.info_till_koordinering_och_operation">
                        {{ anmalan.info_till_koordinering_och_operation }}
                    </li>
                </ul>
            </div>
            <div v-if="isVisible('personId')" :key="`personId-${index}`" class="vg-cell">
                {{ anmalan.patient_display.personId_formatted }}
            </div>
            <div v-if="isVisible('patientnamn')" :key="`patientnamn-${index}`" class="vg-cell">
                {{ anmalan.patient_display.namn_reverse }}
            </div>
        </template>
        <div class="vg-footer-day">
            Summa optid {{ getDay(group.datum) }} {{ moment(group.datum).format("YYYY-MM-DD") }}<br />
            <strong>{{ sumOptid }} min</strong>
        </div>
    </div>
</template>

<script setup>
    import { computed } from "vue";
    import { sumBy } from "lodash";
    import moment from "moment";
    import { useStore } from "@/store";
    import { ConfigModuleName, OperationConfigModuleName } from "@/store/modules/types/configModuleTypes";
    import { getResursWithStatus } from "@/components/forms/reusables/resurserHelper";

    const props = defineProps({
        group: { type: Object, default: () => [] },
    });

    const store = useStore();

    const veckooversiktConfig = computed(() =>
        store.getters["config/configByName"]({
            moduleName: ConfigModuleName.Operation,
            subModuleName: OperationConfigModuleName.OperationVeckooversikt,
        })
    );

    function isVisible(fieldName) {
        const fields = veckooversiktConfig.value?.fields;

        if (!fields || !(fieldName in fields)) {
            return true;
        }

        return store.getters["config/shouldBeVisibleByName"]({
            moduleName: ConfigModuleName.Operation,
            subModuleName: OperationConfigModuleName.OperationVeckooversikt,
            name: fieldName,
        });
    }

    const sumOptid = computed(() => {
        return sumBy(props.group.anmalningar, (anmalan) => anmalan.optid);
    });

    function getDay(str) {
        let date = new Date(str);
        let day = date.toLocaleDateString("sv", { weekday: "long" });
        return day.charAt(0).toUpperCase() + day.slice(1);
    }
</script>

<style lang="scss" scoped>
    @import "@/style/variables";

    /*
     * display: contents makes this element transparent to the parent CSS Grid.
     * Its children (.vg-cell, .vg-footer-day) become direct grid items of the
     * .veckooversikt-grid container, maintaining column alignment across all
     * VeckooversiktList instances without any HTML table structure.
     */
    .veckooversikt-list {
        display: contents;

        .vg-footer-day {
            /* Span all grid columns — replaces colspan="N" */
            grid-column: 1 / -1;
        }

        ul {
            list-style-type: none;
            padding: 0;
            margin: 0;
        }
    }
</style>
